import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Home, FolderOpen, User, LogOut, Menu, X, MessageSquarePlus, History } from 'lucide-react';
import { getImageUrl } from '@/lib/imageUtils';
import { supabase } from '@/integrations/supabase/client';

interface PortalClienteCtx {
  clienteId: string | null;
  clienteNombre: string;
}

const navItems = [
  { to: '/portal', icon: Home, label: 'Inicio', end: true },
  { to: '/portal/avisos', icon: MessageSquarePlus, label: 'Avisos' },
  { to: '/portal/historial', icon: History, label: 'Histórico OT' },
  { to: '/portal/documentos', icon: FolderOpen, label: 'Documentos' },
  { to: '/portal/cuenta', icon: User, label: 'Mi cuenta' },
];

export default function PortalLayout() {
  const { perfil, empresa, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clienteNombre, setClienteNombre] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from('usuarios_portal_cliente')
        .select('cliente_id, clientes(nombre)')
        .eq('user_id', perfil?.user_id)
        .maybeSingle();
      setClienteNombre(data?.clientes?.nombre || '');
    })();
  }, [perfil?.user_id]);

  const logoUrl = empresa?.logo_url ? (getImageUrl(empresa.logo_url) || empresa.logo_url) : null;

  return (
    <div className="min-h-screen bg-muted/20 flex">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-64 flex-col bg-card border-r">
        <div className="p-4 border-b">
          {logoUrl ? (
            <img src={logoUrl} alt={empresa?.nombre || ''} className="h-12 object-contain" />
          ) : (
            <h2 className="font-bold text-lg">{empresa?.nombre || 'Portal'}</h2>
          )}
          {clienteNombre && (
            <p className="text-xs text-muted-foreground mt-2 truncate">{clienteNombre}</p>
          )}
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t space-y-1">
          <p className="text-xs text-muted-foreground px-3 truncate">{perfil?.nombre}</p>
          <Button variant="ghost" size="sm" className="w-full justify-start"
            onClick={async () => { await signOut(); navigate('/portal/login'); }}>
            <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-8 object-contain" />
          ) : (
            <span className="font-bold">{empresa?.nombre || 'Portal'}</span>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => setOpen(v => !v)}>
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-30 bg-background pt-16 p-4" onClick={() => setOpen(false)}>
          <nav className="space-y-2">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md text-base font-medium ${
                    isActive ? 'bg-primary text-primary-foreground' : 'bg-card border'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            ))}
            <Button variant="outline" className="w-full justify-start mt-4"
              onClick={async () => { await signOut(); navigate('/portal/login'); }}>
              <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
            </Button>
          </nav>
        </div>
      )}

      <main className="flex-1 p-4 md:p-8 pt-20 md:pt-8 max-w-5xl mx-auto w-full">
        <Outlet context={{ clienteNombre } satisfies { clienteNombre: string }} />
      </main>
    </div>
  );
}
